import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Button,
  Modal,
  Row,
  Col,
  Select,
  Space,
  Typography,
  Tag,
  Divider,
  Avatar,
  Popconfirm,
  message,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import RolePermissionsEditModal from "../components/RolePermissionEditModal";
import RolePermissionsModal from "../components/RolePermissionModal";
import { ShowToast } from "../helper/toast";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// const PERMISSIONS = ["can_view", "can_create", "can_edit", "can_delete"];

function RoleManagement() {
  const { request } = useRequest();

  const [roles, setRoles] = useState([]);
  const [, setLoading] = useState(false);

  const [roleModalVisible, setRoleModalVisible] = useState(false); // view
  const [roleModalOpen, setRoleModalOpen] = useState(false); // edit
  const [selectedRole, setSelectedRole] = useState(null);

  const [modules, setModules] = useState([]);
  const [activeTab, setActiveTab] = useState("roles");
  const isRolesTab = activeTab === "roles";

  const [usersWithoutRole, setUsersWithoutRole] = useState([]);
  const [assignGlobalModalOpen, setAssignGlobalModalOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState(null);
  const [assignRoleId, setAssignRoleId] = useState(null);
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  const [refreshAssignedUsers, setRefreshAssignedUsers] = useState(0);
  const [roleSubmitting, setRoleSubmitting] = useState(false);

  // const countPermissions = (modules = []) => {
  //   let total = 0;
  //   let stats = { view: 0, create: 0, edit: 0, delete: 0 };

  //   modules.forEach((m) => {
  //     if (m.can_view) {
  //       total++;
  //       stats.view++;
  //     }
  //     if (m.can_create) {
  //       total++;
  //       stats.create++;
  //     }
  //     if (m.can_edit) {
  //       total++;
  //       stats.edit++;
  //     }
  //     if (m.can_delete) {
  //       total++;
  //       stats.delete++;
  //     }
  //   });

  //   return { total, stats };
  // };

  const fetchModules = () => {
    request({
      url: "/api/admin/modules",
      method: "GET",
      onSuccess: (res) => setModules(res || []),
    });
  };

  const handleDeleteRole = (roleId) => {
    request({
      url: `/api/admin/roles/${roleId}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Role deleted successfully");
        fetchRoles();
        setRoleModalVisible(false);
        setRoleModalOpen(false);
        setSelectedRole(null);
      },
      onError: () => {
        message.error("Failed to delete role");
      },
    });
  };

  const fetchUsersWithoutRole = () => {
    request({
      url: "/api/admin/users?role=none", // backend should return users with no role
      method: "GET",
      onSuccess: (res) => {
        setUsersWithoutRole(res?.data || []);
      },
    });
  };

  const handleTabChange = (key) => {
    if (key === activeTab) return;

    setActiveTab(key);

    if (key === "roles") fetchRoles();
    if (key === "assign") setRefreshAssignedUsers((p) => p + 1);
  };

  /* ---------------- FETCH ROLES ---------------- */
  const fetchRoles = () => {
    setLoading(true);
    request({
      url: "/api/admin/roles",
      method: "GET",
      onSuccess: (res) => {
        setRoles(res?.data || []);
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  };

  const StatCard = ({ label, value, bg, color }) => (
    <div
      style={{
        background: bg,
        borderRadius: 12,
        padding: "12px 10px",
        textAlign: "center",
        border: "1px solid rgba(15,23,42,.08)",
      }}
    >
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      <Title level={4} style={{ margin: 0, color, fontWeight: 700 }}>
        {value}
      </Title>
    </div>
  );

  useEffect(() => {
    fetchRoles();
    fetchModules();
  }, []);

  const actionButton = {
    label: isRolesTab ? "Add Role" : "Assign Role",
    onClick: () => {
      if (isRolesTab) {
        setSelectedRole(null);
        setRoleModalOpen(true);
      } else {
        fetchUsersWithoutRole();
        setAssignUserId(null);
        setAssignRoleId(null);
        setAssignGlobalModalOpen(true);
      }
    },
    style: isRolesTab
      ? {
        borderRadius: 12,
        fontWeight: 600,
      }
      : {
        borderRadius: 12,
        fontWeight: 600,
        background: "linear-gradient(135deg, #22c55e, #16a34a)",
        border: "none",
      },
  };

  /* ---------------- TABLE ---------------- */

  return (
    <Card
      bordered={false}
      title={
        <>
          <Title level={4} style={{ marginBottom: 0, fontWeight: 700 }}>
            Roles & Permissions
          </Title>
          <Text type="secondary">
            Create roles, define permissions and assign them to users
          </Text>
        </>
      }
      extra={
        isRolesTab && (
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            style={{
              ...actionButton.style,
              whiteSpace: "nowrap",
            }}
            onClick={actionButton.onClick}
          >
            {actionButton.label}
          </Button>
        )
      }
    >
      {/* <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        animated
        size="large"
        defaultActiveKey="roles"
        className="role-tabs"
        type="card"
        tabBarStyle={{
          marginBottom: 20,
          padding: 6,
          background: "#f8fafc",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
        }}
        tabBarGutter={8}
      > */}
      {/* ================= TAB 1 ================= */}
      {/* <TabPane
          key="roles"
          tab={
            <Space size={8}>
              <span style={{ fontWeight: 600 }}>Roles & Permissions</span>
              <Tag color="blue">{roles.length}</Tag>
            </Space>
          }
        > */}
      <Row gutter={[18, 18]}>
        {roles.map((role) => (
          <Col xs={24} sm={12} xxl={8} key={role.id}>
            <Card
              hoverable
              style={{
                height: "100%",
                borderRadius: 16,
                border: "1px solid rgba(15,23,42,.08)",
                overflow: "hidden",
              }}
              bodyStyle={{ padding: 0 }}
            >
              {/* ===== Header ===== */}
              <div
                style={{
                  padding: "16px 18px",
                  background: "#f8fbff",
                  borderBottom: "1px solid rgba(15,23,42,.06)",
                }}
              >
                <Space
                  direction="vertical"
                  size={6}
                  style={{ width: "100%" }}
                >
                  <Space align="center">
                    <Title level={4} style={{ margin: 0, fontSize: 24 }}>
                      {role.name}
                    </Title>

                    {role.assigned_users_count > 0 && (
                      <Tag color="gold">In Use</Tag>
                    )}
                  </Space>

                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Role permission configuration
                  </Text>
                </Space>
              </div>

              {/* ===== Content ===== */}
              <div style={{ padding: 16 }}>
                {/* ===== Stats ===== */}
                <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
                  <Col span={8}>
                    <StatCard
                      label="Modules"
                      value={`${role.module_permissions.length} / ${modules.length}`}
                      bg="#eff6ff"
                      color="#0369a1"
                    />
                  </Col>

                  <Col span={8}>
                    <StatCard
                      label="Permissions"
                      value={role.module_permissions.reduce(
                        (t, m) =>
                          t +
                          m.can_view +
                          m.can_create +
                          m.can_edit +
                          m.can_delete,
                        0,
                      )}
                      bg="#ecfdf3"
                      color="#166534"
                    />
                  </Col>

                  <Col span={8}>
                    <StatCard
                      label="Users"
                      value={role.assigned_users_count}
                      bg={
                        role.assigned_users_count > 0
                          ? "#fff7e6"
                          : "#f8fafc"
                      }
                      color="#92400e"
                    />
                  </Col>
                </Row>

                {/* ===== Permissions ===== */}
                <Row gutter={[8, 8]}>
                  {[
                    ["View", "blue", "can_view"],
                    ["Create", "green", "can_create"],
                    ["Edit", "orange", "can_edit"],
                    ["Delete", "red", "can_delete"],
                  ].map(([label, color, key]) => (
                    <Col span={12} key={key}>
                      <Tag
                        color={color}
                        style={{
                          width: "100%",
                          textAlign: "center",
                          padding: "4px 0",
                          marginRight: 0,
                          borderRadius: 8,
                          fontWeight: 600,
                        }}
                      >
                        {label}{" "}
                        {
                          role.module_permissions.filter((m) => m[key])
                            .length
                        }
                      </Tag>
                    </Col>
                  ))}
                </Row>

                {/* ===== Actions ===== */}
                <Row gutter={[8, 8]} className="mt-1" >
                  <Col span={12}>
                    <Button
                      block
                      size="middle"
                      style={{
                        borderRadius: 10,
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        color: "#1d4ed8",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setSelectedRole(role);
                        setRoleModalVisible(true);
                      }}
                    >
                      View
                    </Button>
                  </Col>

                  <Col span={12}>
                    <Button
                      block
                      size="middle"
                      type="primary"
                      style={{
                        borderRadius: 10,
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setRoleModalOpen(false);
                        setTimeout(() => {
                          setSelectedRole(role);
                          setRoleModalOpen(true);
                        }, 0);
                      }}
                    >
                      Manage
                    </Button>
                  </Col>
                </Row>

                {/* ===== Danger Zone ===== */}
                <Tooltip
                  title={
                    role.assigned_users_count > 0
                      ? "You cannot delete this role because users are assigned to it."
                      : ""
                  }
                >
                  <span>
                    <Popconfirm
                      title="Delete this role?"
                      description={
                        role.assigned_users_count > 0
                          ? "This role has users assigned."
                          : "This action cannot be undone."
                      }
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      disabled={role.assigned_users_count > 0}
                      onConfirm={() => handleDeleteRole(role.id)}
                    >
                      <Button
                        danger
                        block
                        icon={<DeleteOutlined />}
                        disabled={role.assigned_users_count > 0}
                        style={{
                          marginTop: 10,
                          borderRadius: 10,
                          fontWeight: 600,
                          marginRight: "5px",
                        }}
                      >
                        Delete Role
                      </Button>
                    </Popconfirm>
                  </span>
                </Tooltip>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      {/* </TabPane> */}

      {/* ================= TAB 2 ================= */}
      {/* <TabPane
          key="assign"
          tab={
            <Space size={8}>
              <span style={{ fontWeight: 600 }}>Assign Role</span>
              <Tag color="green">Manage</Tag>
            </Space>
          }
        >
          <AssignRoleTab roles={roles} refreshKey={refreshAssignedUsers} />
        </TabPane> */}
      {/* </Tabs> */}

      {/* ================= ROLE MODAL ================= */}
      <RolePermissionsModal
        open={roleModalVisible}
        role={selectedRole}
        onClose={() => {
          setRoleModalVisible(false);
          setSelectedRole(null);
        }}
      />

      <RolePermissionsEditModal
        open={roleModalOpen}
        role={selectedRole} // null for add
        modules={modules}
        onClose={() => setRoleModalOpen(false)}
        onSubmit={(values) => {
          setRoleSubmitting(true);
          request({
            url: selectedRole
              ? `/api/admin/roles/${selectedRole.id}`
              : "/api/admin/roles",
            method: selectedRole ? "PUT" : "POST",
            data: values,
            onSuccess: (res) => {
              fetchRoles();
              setRoleModalOpen(false);
              setRoleSubmitting(false);
              ShowToast("Role updated successfully", "success");
              const savedUserData = localStorage.getItem("user");
              if (savedUserData) {
                const userData = JSON.parse(savedUserData);
                if (res?.module_permissions) {
                  localStorage.setItem("user", JSON.stringify({
                    ...userData,
                    role: {
                      ...userData.role,
                      module_permissions: res.module_permissions
                    },
                  }));
                }
              }
            },
            onFinally: () => {
              setRoleSubmitting(false);
            },
          });
        }}
        onDelete={() => handleDeleteRole(selectedRole.id)}
        loading={roleSubmitting}
      />

      {/* ================= ASSIGN ROLE MODAL ================= */}
      <Modal
        open={assignGlobalModalOpen}
        centered
        destroyOnClose
        footer={null}
        width={560}
        closable={false}
        onCancel={() => setAssignGlobalModalOpen(false)}
      >
        {/* ================= HEADER ================= */}
        <div
          style={{
            padding: "24px 28px",
            margin: "-24px -24px 20px",
            background: "linear-gradient(135deg, #13a6eb, #36baf7)",
            color: "#fff",
            borderRadius: "14px 14px 0 0",
          }}
        >
          <Space align="center" size={16}>
            <Avatar
              size={54}
              style={{
                background: "#fff",
                color: "#2563eb",
                fontWeight: 800,
                fontSize: 22,
              }}
            >
              👤
            </Avatar>

            <div>
              <Title level={4} style={{ margin: 0, color: "#fff" }}>
                Assign Role
              </Title>
              <Text style={{ color: "rgba(255,255,255,.85)" }}>
                Select a user and assign a role
              </Text>
            </div>
          </Space>
        </div>

        {/* ================= BODY ================= */}
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          {/* USER SELECTION */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              background: "linear-gradient(135deg, #f8fafc, #eef2f7)",
              boxShadow: "0 8px 24px rgba(0,0,0,.08)",
              width: "100%",
            }}
          >
            <Text type="secondary">User</Text>
            <Select
              showSearch
              placeholder="Select user"
              value={assignUserId}
              onChange={setAssignUserId}
              size="large"
              style={{ width: "100%", marginTop: 6 }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={(Array.isArray(usersWithoutRole)
                ? usersWithoutRole
                : []
              ).map((u) => ({
                value: u.id,
                label: (
                  <Space>
                    <Avatar
                      size={26}
                      style={{
                        background: "linear-gradient(135deg, #38bdf8, #0ea5e9)",
                      }}
                    >
                      {u.full_name?.[0]}
                    </Avatar>
                    <span style={{ fontWeight: 600 }}>{u.full_name}</span>
                    <Text type="secondary">({u.email})</Text>
                  </Space>
                ),
              }))}
            />
          </Card>

          {/* ROLE SELECTION */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              background: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
              boxShadow: "0 8px 24px rgba(0,0,0,.08)",
              width: "100%",
            }}
          >
            <Text type="secondary">Role</Text>
            <Select
              showSearch
              placeholder="Select role"
              value={assignRoleId}
              onChange={setAssignRoleId}
              size="large"
              style={{ width: "100%", marginTop: 6 }}
              optionFilterProp="label"
              options={roles.map((r) => ({
                value: r.id,
                label: (
                  <Space>
                    <Avatar
                      size={26}
                      style={{
                        background: "linear-gradient(135deg, #22c55e, #16a34a)",
                      }}
                    >
                      {r.name[0]}
                    </Avatar>
                    <span style={{ fontWeight: 600 }}>{r.name}</span>
                  </Space>
                ),
              }))}
            />
          </Card>
        </Space>

        {/* ================= FOOTER ================= */}
        <Divider style={{ margin: "22px 0 14px" }} />

        <Row justify="end" gutter={12}>
          <Col>
            <Button
              size="large"
              onClick={() => setAssignGlobalModalOpen(false)}
              style={{ borderRadius: 12 }}
            >
              Cancel
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              size="large"
              loading={assignSubmitting}
              disabled={!assignUserId || !assignRoleId}
              onClick={() => {
                setAssignSubmitting(true);

                request({
                  url: "/api/admin/assign-role",
                  method: "POST",
                  data: {
                    user_id: assignUserId,
                    role_id: assignRoleId,
                  },
                  onSuccess: () => {
                    message.success("Role assigned successfully");
                    setAssignGlobalModalOpen(false);
                    setAssignUserId(null);
                    setAssignRoleId(null);
                    setRefreshAssignedUsers((prev) => prev + 1);
                  },
                  onFinally: () => setAssignSubmitting(false),
                });
              }}
              style={{
                borderRadius: 12,
                fontWeight: 600,
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                border: "none",
                boxShadow: "0 10px 24px rgba(34,197,94,.35)",
              }}
            >
              Assign Role
            </Button>
          </Col>
        </Row>
      </Modal>
    </Card>
  );
}

function AssignRoleTab({ roles, refreshKey }) {
  const { request } = useRequest();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const fetchAssignedUsers = () => {
    request({
      url: "/api/admin/sub-admins",
      method: "GET",
      onSuccess: (res) => setUsers(res?.data || []),
    });
  };

  useEffect(() => {
    fetchAssignedUsers();
  }, [refreshKey]);

  const handleRemoveRole = (userId) => {
    request({
      url: "/api/admin/remove-role",
      method: "POST",
      data: {
        user_id: userId,
      },
      onSuccess: () => {
        message.success("Role removed successfully");
        fetchAssignedUsers(); // refresh cards
      },
      onError: () => {
        message.error("Failed to remove role");
      },
    });
  };

  const handleAssignRole = () => {
    request({
      url: "/api/admin/assign-role",
      method: "POST",
      data: {
        user_id: selectedUser.id,
        role_id: selectedRoleId,
      },
      onSuccess: () => {
        fetchAssignedUsers();
        setAssignModalOpen(false);
        setSelectedUser(null);
        setSelectedRoleId(null);
      },
    });
  };

  return (
    <>
      <Row gutter={[24, 24]}>
        {users.map((u) => (
          <Col xs={24} sm={12} lg={8} key={u.id}>
            <Card
              hoverable
              style={{
                borderRadius: 20,
                border: "1px solid rgba(15,23,42,0.08)",
                boxShadow: "0 30px 60px rgba(2,8,23,.12)",
                transition: "all .3s ease",
              }}
              bodyStyle={{ padding: 24 }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-6px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              {/* ===== TOP ===== */}
              <Space align="center" size={14}>
                <Avatar
                  size={48}
                  style={{
                    background:
                      "linear-gradient(135deg, #38bdf8, #0ea5e9, #0284c7)",
                    fontWeight: 700,
                  }}
                >
                  {u.full_name?.[0]}
                </Avatar>

                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    {u.full_name}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {u.email}
                  </Text>
                </div>
              </Space>

              {/* ===== META ===== */}
              <Divider style={{ margin: "16px 0" }} />

              <Space size={8} wrap>
                <Tag color="blue">{u.user_type}</Tag>
                <Tag color={u.status === "Active" ? "green" : "red"}>
                  {u.status}
                </Tag>
                <Tag color="purple">{u.role?.name || "No Role Assigned"}</Tag>
              </Space>

              {/* ===== ACTIONS ===== */}
              <Divider style={{ margin: "16px 0" }} />

              <Row gutter={12}>
                <Col span={8}>
                  <Button
                    block
                    style={{
                      borderRadius: 12,
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      setSelectedUser(u);
                      setSelectedRoleId(u.role?.id || null);
                      setAssignModalOpen(true);
                    }}
                  >
                    Change
                  </Button>
                </Col>

                <Col span={8}>
                  <Button
                    block
                    type="primary"
                    style={{
                      borderRadius: 12,
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      setSelectedUser(u);
                      setViewModalOpen(true);
                    }}
                  >
                    View
                  </Button>
                </Col>

                <Col span={8}>
                  <Popconfirm
                    title="Remove assigned role?"
                    description="This will remove the role from the user."
                    okText="Yes, Remove"
                    cancelText="Cancel"
                    onConfirm={() => handleRemoveRole(u.id)}
                    disabled={!u.role}
                  >
                    <Button
                      block
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!u.role}
                      style={{
                        borderRadius: 12,
                        fontWeight: 600,
                      }}
                    >
                      Remove
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        closable={false}
        width={900}
        centered
        destroyOnClose
        footer={[
          <Button
            key="done"
            type="primary"
            size="large"
            onClick={() => setViewModalOpen(false)}
          >
            Done
          </Button>,
        ]}
      >
        {selectedUser && (
          <>
            {/* ================= HEADER ================= */}
            <div
              style={{
                padding: "28px 36px",
                margin: "-24px -24px 0",
                background:
                  "linear-gradient(135deg, #38bdf8, #0ea5e9, #0284c7)",
                color: "#fff",
              }}
            >
              <Space align="center" size={20}>
                <Avatar
                  size={72}
                  style={{
                    background: "#fff",
                    color: "#0284c7",
                    fontWeight: 800,
                    fontSize: 26,
                  }}
                >
                  {selectedUser.full_name?.[0]}
                </Avatar>

                <div>
                  <Title level={4} style={{ margin: 0, color: "#fff" }}>
                    {selectedUser.full_name}
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,.9)" }}>
                    {selectedUser.email}
                  </Text>

                  <div style={{ marginTop: 10 }}>
                    <Tag color="blue">{selectedUser.user_type}</Tag>
                    <Tag
                      color={selectedUser.status === "Active" ? "green" : "red"}
                    >
                      {selectedUser.status}
                    </Tag>
                  </div>
                </div>
              </Space>
            </div>

            {/* ================= BODY ================= */}
            <div
              style={{
                padding: 28,
                maxHeight: "65vh",
                overflowY: "auto",
                background: "linear-gradient(180deg, #f8fafc, #eef2f7)",
              }}
            >
              {/* ===== USER INFO GRID ===== */}
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  marginBottom: 28,
                  background: "#ffffff",
                  boxShadow: "0 8px 20px rgba(0,0,0,.06)",
                }}
              >
                <Title level={5} style={{ marginBottom: 16 }}>
                  User Information
                </Title>

                <Row gutter={[24, 20]}>
                  {[
                    ["Username", selectedUser.username],
                    [
                      "Mobile",
                      `${selectedUser.country_code} ${selectedUser.mobile}`,
                    ],
                    ["Date of Birth", selectedUser.date_of_birth || "—"],
                    ["Gender", selectedUser.gender || "—"],
                    ["Blood Group", selectedUser.blood_group || "—"],
                    ["Last Login", selectedUser.last_login || "—"],
                  ].map(([label, value]) => (
                    <Col span={12} key={label}>
                      <Text type="secondary">{label}</Text>
                      <div style={{ fontWeight: 600, marginTop: 4 }}>
                        {value}
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>

              {/* ===== ROLE ===== */}
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  marginBottom: 28,
                  background: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
                }}
              >
                <Title level={5}>Assigned Role</Title>
                <Tag
                  color="purple"
                  style={{
                    padding: "8px 18px",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {selectedUser.role?.name || "No Role Assigned"}
                </Tag>
              </Card>

              {/* ===== MODULE PERMISSIONS ===== */}
              <Title level={5} style={{ marginBottom: 16 }}>
                Module Permissions
              </Title>

              <Row gutter={[20, 20]}>
                {selectedUser.role?.module_permissions
                  ?.filter(
                    (p) =>
                      p.can_view || p.can_create || p.can_edit || p.can_delete,
                  )
                  .map((p) => {
                    const moduleName = typeof p.module === "string"
                      ? p.module.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                      : p.module?.name;
                    const firstChar = moduleName ? moduleName[0] : "";

                    return (
                      <Col span={12} key={p.id}>
                        <Card
                          bordered={false}
                          hoverable
                          style={{
                            borderRadius: 16,
                            height: "100%",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.transform = "translateY(-4px)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.transform = "translateY(0)")
                          }
                        >
                          {/* MODULE HEADER */}
                          <Space
                            align="center"
                            size={12}
                            style={{ marginBottom: 14 }}
                          >
                            <Avatar
                              size={38}
                              style={{
                                background:
                                  "linear-gradient(135deg, #2563eb, #3b82f6)",
                                fontWeight: 600,
                              }}
                            >
                              {firstChar}
                            </Avatar>

                            <div>
                              <Title level={5} style={{ margin: 0 }}>
                                {moduleName}
                              </Title>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Module permissions
                              </Text>
                            </div>
                          </Space>

                        <Divider style={{ margin: "12px 0" }} />

                        {/* PERMISSIONS */}
                        <Space wrap>
                          {[
                            ["View", p.can_view],
                            ["Create", p.can_create],
                            ["Edit", p.can_edit],
                            ["Delete", p.can_delete],
                          ].map(([label, enabled]) => (
                            <Tag
                              key={label}
                              icon={
                                enabled ? <CheckOutlined /> : <CloseOutlined />
                              }
                              style={{
                                padding: "6px 14px",
                                borderRadius: 20,
                                border: "none",
                                fontWeight: 500,
                                background: enabled
                                  ? "linear-gradient(135deg, #22c55e, #16a34a)"
                                  : "#e5e7eb",
                                color: enabled ? "#fff" : "#6b7280",
                              }}
                            >
                              {label}
                            </Tag>
                          ))}
                        </Space>
                      </Card>
                    </Col>
                    );
                  })}

                {!selectedUser.role?.module_permissions?.some(
                  (p) =>
                    p.can_view || p.can_create || p.can_edit || p.can_delete,
                ) && (
                    <Text type="secondary">
                      No permissions assigned to this role.
                    </Text>
                  )}
              </Row>
            </div>
          </>
        )}
      </Modal>

      {/* ASSIGN ROLE MODAL */}
      <Modal
        open={assignModalOpen}
        centered
        destroyOnClose
        closable={false}
        footer={null}
        width={520}
        onCancel={() => setAssignModalOpen(false)}
      >
        {/* ===== HEADER ===== */}
        <div
          style={{
            padding: "22px 26px",
            margin: "-24px -24px 20px",
            background: "linear-gradient(135deg, #13a6eb, #34b9f7)",
            color: "#fff",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Space size={16} align="center">
            <Avatar
              size={56}
              style={{
                background: "#fff",
                color: "#2563eb",
                fontWeight: 700,
                fontSize: 22,
              }}
            >
              {selectedUser?.full_name?.[0]}
            </Avatar>

            <div>
              <Title level={4} style={{ margin: 0, color: "#fff" }}>
                Assign Role
              </Title>
              <Text style={{ color: "rgba(255,255,255,.85)" }}>
                {selectedUser?.full_name}
              </Text>
            </div>
          </Space>
        </div>

        {/* ===== BODY ===== */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            background: "linear-gradient(180deg, #f8fafc, #eef2f7)",
            boxShadow: "0 10px 30px rgba(0,0,0,.08)",
          }}
        >
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div>
              <Text type="secondary">Select Role</Text>
              <Select
                placeholder="Choose a role to assign"
                value={selectedRoleId}
                onChange={setSelectedRoleId}
                size="large"
                style={{ width: "100%", marginTop: 6 }}
                options={roles.map((r) => ({
                  label: (
                    <Space>
                      <Avatar
                        size={26}
                        style={{
                          background:
                            "linear-gradient(135deg, #38bdf8, #0ea5e9)",
                        }}
                      >
                        {r.name[0]}
                      </Avatar>
                      <span style={{ fontWeight: 600 }}>{r.name}</span>
                    </Space>
                  ),
                  value: r.id,
                }))}
              />
            </div>
          </Space>
        </Card>

        {/* ===== FOOTER ===== */}
        <Divider style={{ margin: "22px 0 14px" }} />

        <Row justify="end" gutter={12}>
          <Col>
            <Button
              size="large"
              onClick={() => setAssignModalOpen(false)}
              style={{ borderRadius: 12 }}
            >
              Cancel
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              size="large"
              disabled={!selectedRoleId}
              onClick={handleAssignRole}
              style={{
                borderRadius: 12,
                fontWeight: 600,
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                border: "none",
                boxShadow: "0 10px 20px rgba(34,197,94,.35)",
              }}
            >
              Assign Role
            </Button>
          </Col>
        </Row>
      </Modal>
    </>
  );
}

export default RoleManagement;
